import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  fetchUncategorizedAssets,
  postRequestSignedUrls
} from '../../../assets/requests'
import {
  createNavigationHandlers,
  sortAssetsByDate,
  removeAssetFromArray,
  createModalProps,
  handleAsyncError
} from './utils/categoryUtils'
import placeholderImg from '../../../assets/images/camera.jpg'
import '../../../assets/stylesheets/gallery-style.css'
import '../../../assets/stylesheets/assets.css'
import '../../../assets/stylesheets/duplicates.css'
import '../../../assets/stylesheets/modal.css'
import { handleModalOpen } from '../Modal/modals'
import { ModalHtml } from '../Modal/modals'
import upArrow from '../../../assets/images/up-arrow.png'
import backArrow from '../../../assets/images/back-arrow3.png'
import homeIcon from '../../../assets/images/home-icon.png'
import { useUserInfo } from '../../Authentication/hooks'

const UncategorizedAssets = () => {
  const [assets, setAssets] = useState([])
  const [modalActive, setModalActive] = useState(false)
  const [currentAssetId, setCurrentAssetId] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const dispatch = useDispatch()
  const { userId } = useUserInfo()

  const navigate = useNavigate()

  const { handleHomeClick, handleBackClick, handleBackToTop } =
    createNavigationHandlers(navigate)

  // Sign individual asset thumbnails for display
  const signAssets = useCallback(
    async assets => {
      if (!Array.isArray(assets) || assets.length === 0 || !userId) {
        return assets
      }

      let thumbnailKeys = []
      let s3Uris = []
      let s3UriIdx = []

      assets.forEach((asset, idx) => {
        if (!asset.thumbnailUrl && asset.thumbnails && asset.thumbnails.length > 0) {
          // TODO: don't assume index 1 is always the thumbnail with the desired size - choose it
          thumbnailKeys.push(asset.thumbnails[1])
        }

        if (!asset.fileUrl) {
          s3Uris.push(asset.s3uri)
          s3UriIdx.push(idx)
        }
      })

      const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
        thumbnailKeys.length > 0
          ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
          : [],
        s3Uris.length > 0
          ? postRequestSignedUrls({ userId, payload: s3Uris })
          : []
      ])

      // Update each asset with signed URLs
      assets.forEach((asset, idx) => {
        if (signedThumbnailUrls[idx]) {
          asset.thumbnailUrl = signedThumbnailUrls[idx]
        } else {
          asset.thumbnailUrl = placeholderImg
        }

        // set fileUrl if we signed s3uri (use the mapping index)
        const s3UriSignedIndex = s3UriIdx.indexOf(idx)
        if (
          s3UriSignedIndex !== -1 &&
          signedS3Uris &&
          signedS3Uris[s3UriSignedIndex]
        ) {
          asset.fileUrl = signedS3Uris[s3UriSignedIndex]
        }
      })

      return assets
    },
    [userId]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError('')

        const rawUncategorized = await fetchUncategorizedAssets({ userId, assetType: 'image' })

        if (rawUncategorized && rawUncategorized.length > 0) {
          const signedAssets = await signAssets(rawUncategorized)
          const sortedAssets = sortAssetsByDate(signedAssets, 'asc')
          setAssets(sortedAssets)
        } else {
          setAssets([])
        }
      } catch (error) {
        const errorMessage = handleAsyncError(error, 'fetching assets')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [userId, signAssets, refreshTrigger])

  if (error) {
    return <div className="error-message">Error: {error}</div>
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (!assets.length) {
    return (
      <div className="duplicates-category-container">
        <h2 className="row-title-duplicates">Uncategorized</h2>
        <div className="go-back">
          <img
            className="nav-icon-home home fav"
            alt="Go to base of navigation"
            src={homeIcon}
            onClick={handleHomeClick}
          />
          <img
            className="nav-icon-back back"
            alt="Go back one level."
            src={backArrow}
            onClick={handleBackClick}
          />
          <img
            className="nav-icon-top top"
            alt="Go back to top."
            src={upArrow}
            onClick={handleBackToTop}
          />
        </div>
        <div className="gallery-container">
          <div className="empty-state-message">
            <p>No uncategorized images found in your collection.</p>
          </div>
        </div>
      </div>
    )
  }

  const closeModal = () => {
    setModalActive(false)
  }

  const handleAssetDeleted = deletedAssetId => {
    const updatedAssets = removeAssetFromArray(assets, deletedAssetId)
    setAssets(updatedAssets)
    closeModal()
    setRefreshTrigger(!refreshTrigger)
  }

  return (
    <div className="duplicates-category-container">
      <h2 className="row-title-duplicates">Uncategorized</h2>
      <div className="go-back">
        <img
          className="nav-icon-home home fav"
          alt="Go to base of navigation"
          src={homeIcon}
          onClick={handleHomeClick}
        />
        <img
          className="nav-icon-back back"
          alt="Go back one level."
          src={backArrow}
          onClick={handleBackClick}
        />
        <img
          className="nav-icon-top top"
          alt="Go back to top."
          src={upArrow}
          onClick={handleBackToTop}
        />
      </div>

      <div className="gallery-container">
        {assets.map(asset => (
          <div key={asset.id} className="gallery-thumb-duplicates">
            <div
              className="gallery-thumb2"
              style={{
                backgroundImage: `url('${asset.thumbnailUrl || placeholderImg}')`
              }}
              onClick={() => {
                setCurrentAssetId(asset.id)
                const modalProps = createModalProps(
                  dispatch,
                  modalActive,
                  assets,
                  asset.id
                )

                handleModalOpen(modalProps)
                setModalActive(true)
              }}
            >
            </div>
          </div>
        ))}
      </div>
      {modalActive && (
        <ModalHtml
          state={{
            modalActive,
            currentAsset: currentAssetId,
            modalStatus: 'Image',
            assets
          }}
          onAssetDeleted={handleAssetDeleted}
          dispatch={dispatch}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default UncategorizedAssets
